import { queryOptions, useMutation } from '@tanstack/react-query';
import { format } from 'date-fns';

import type { AccessToken } from '@/lib/tokenStorage';
import { useAccessToken } from '@/services/auth';
import { translateInactiveSireErrors } from '@/utils/sire';

import {
  CommentResponse,
  CreatedFertResponse,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  FertilizationDonorScheduleResponse,
  FertPulledSemenResponse,
  FertSiteResponse,
  ScheduledDonorResponse,
  TechResponse,
  UpsertReverseSortRequest,
  UpsertReverseSortResponse,
} from './types';

export type {
  CommentResponse as CommentItem,
  CreateFertilizationRequest,
  CreateFertilizationResponse,
  CreatedFertResponse as CreatedFertItem,
  FertilizationDonorScheduleResponse as FertDonorScheduleItem,
  FertPulledSemenResponse as FertPulledSemenItem,
  FertSiteResponse as FertSiteItem,
  ScheduledDonorResponse as FertScheduledDonorItem,
  TechResponse as TechItem,
  UpsertReverseSortRequest,
  UpsertReverseSortResponse,
} from './types';

export type EditFertilizationRequest = {
  fertilizationId: string;
  transferClient?: number | null;
  frozeClient?: number | null;
  discardClient?: number | null;
  shippedBillable?: number | null;
};

export type EditFertilizationPayload = {
  fertilizationId: string;
  success: boolean;
  message?: string | null;
};

function getBaseUrl(): string {
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
  if (
    !API_BASE_URL ||
    API_BASE_URL === 'undefined' ||
    API_BASE_URL === 'null'
  ) {
    throw new Error('VITE_API_BASE_URL is not defined or invalid');
  }
  return API_BASE_URL;
}

function getHeaders(token: AccessToken): HeadersInit {
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function toDateString(date: string | Date): string {
  if (date instanceof Date) {
    return format(date, 'yyyy-MM-dd');
  }
  return date;
}

async function fetchGet<T>(
  token: AccessToken,
  path: string,
  params?: URLSearchParams,
): Promise<T> {
  const url = params
    ? `${getBaseUrl()}/api/v1/fertilization/${path}?${params.toString()}`
    : `${getBaseUrl()}/api/v1/fertilization/${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: getHeaders(token),
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchMutation<T>(
  token: AccessToken,
  path: string,
  method: 'POST' | 'PUT',
  body: unknown,
): Promise<T> {
  const url = `${getBaseUrl()}/api/v1/fertilization/${path}`;

  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: getHeaders(token),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `HTTP error! status: ${response.status}`);
  }

  return (await response.json()) as T;
}

async function fetchCreatedFerts(
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
): Promise<CreatedFertResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<CreatedFertResponse[]>(token, 'created-ferts', searchParams);
}

async function fetchPulledSemen(
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
): Promise<FertPulledSemenResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<FertPulledSemenResponse[]>(
    token,
    'pulled-semen',
    searchParams,
  );
}

async function fetchDonorSchedules(
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
): Promise<FertilizationDonorScheduleResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<FertilizationDonorScheduleResponse[]>(
    token,
    'donor-schedules',
    searchParams,
  );
}

async function fetchScheduledDonors(
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    semenId?: string | null;
    accountId?: string | null;
    semenType?: string;
  },
): Promise<ScheduledDonorResponse[]> {
  const searchParams = new URLSearchParams();
  searchParams.set('donorScheduleDate', toDateString(params.donorScheduleDate));
  if (params.semenId) searchParams.set('semenId', params.semenId);
  if (params.accountId) searchParams.set('accountId', params.accountId);
  if (params.semenType) searchParams.set('semenType', params.semenType);
  return fetchGet<ScheduledDonorResponse[]>(
    token,
    'scheduled-donors',
    searchParams,
  );
}

async function fetchFertSites(token: AccessToken): Promise<FertSiteResponse[]> {
  return fetchGet<FertSiteResponse[]>(token, 'fert-sites');
}

async function fetchTechs(
  token: AccessToken,
  params: {
    fertSiteId?: string | null;
  },
): Promise<TechResponse[]> {
  const searchParams = new URLSearchParams();
  if (params.fertSiteId) searchParams.set('fertSiteId', params.fertSiteId);
  return fetchGet<TechResponse[]>(
    token,
    'techs',
    searchParams.toString() ? searchParams : undefined,
  );
}

async function fetchComments(token: AccessToken): Promise<CommentResponse[]> {
  return fetchGet<CommentResponse[]>(token, 'comments');
}

async function fetchCommentsInGrid(
  token: AccessToken,
): Promise<CommentResponse[]> {
  return fetchGet<CommentResponse[]>(token, 'comments-in-grid');
}

export const createdFertsOptions = (
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['fertilization', 'created-ferts', params],
    async queryFn() {
      return fetchCreatedFerts(token, params);
    },
    enabled: !!token,
  });

export const fertPulledSemenOptions = (
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['fertilization', 'pulled-semen', params],
    async queryFn() {
      return fetchPulledSemen(token, params);
    },
    enabled: !!token,
  });

export const fertDonorSchedulesOptions = (
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    fertSiteId: string;
  },
) =>
  queryOptions({
    queryKey: ['fertilization', 'donor-schedules', params],
    async queryFn() {
      return fetchDonorSchedules(token, params);
    },
    enabled: !!token,
  });

export const fertScheduledDonorsOptions = (
  token: AccessToken,
  params: {
    donorScheduleDate: string;
    semenId?: string | null;
    accountId?: string | null;
    semenType?: string;
  },
) =>
  queryOptions({
    queryKey: ['fertilization', 'scheduled-donors', params],
    async queryFn() {
      return fetchScheduledDonors(token, params);
    },
    enabled: !!token,
  });

export const fertSitesOptions = (token: AccessToken) =>
  queryOptions({
    queryKey: ['fertilization', 'fert-sites'],
    async queryFn() {
      return fetchFertSites(token);
    },
    enabled: !!token,
  });

export const fertTechsOptions = (
  token: AccessToken,
  params: { fertSiteId?: string | null },
) =>
  queryOptions({
    queryKey: ['fertilization', 'techs', params],
    async queryFn() {
      return fetchTechs(token, params);
    },
    enabled: !!token,
  });

export const fertCommentsOptions = (token: AccessToken) =>
  queryOptions({
    queryKey: ['fertilization', 'comments'],
    async queryFn() {
      return fetchComments(token);
    },
    enabled: !!token,
  });

export const fertCommentsInGridOptions = (token: AccessToken) =>
  queryOptions({
    queryKey: ['fertilization', 'comments-in-grid'],
    async queryFn() {
      return fetchCommentsInGrid(token);
    },
    enabled: !!token,
  });

export function useCreateFertilizationRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (request: CreateFertilizationRequest) => {
      const result = await fetchMutation<CreateFertilizationResponse>(
        token,
        '',
        'POST',
        request,
      );
      return { ...result, success: result.success ?? true };
    },
  });
}

export function useCreateFertilizationV2RestMutation() {
  const token = useAccessToken();
  return useMutation({
    async mutationFn(
      request: CreateFertilizationRequest,
    ): Promise<CreateFertilizationResponse> {
      const response = await fetch(`${getBaseUrl()}/api/v2/fertilization`, {
        method: 'POST',
        credentials: 'include',
        headers: getHeaders(token),
        body: JSON.stringify(request),
      });

      if (response.status === 400) {
        const body = await response.text();
        return {
          code: 400,
          success: false,
          message: translateInactiveSireErrors(body) ?? body,
        };
      }

      if (!response.ok) {
        const body = await response.text();
        throw new Error(body || `HTTP error! status: ${response.status}`);
      }

      const result = (await response.json()) as CreateFertilizationResponse;
      return { ...result, success: result.success ?? true };
    },
  });
}

export function useUpsertReverseSortRestMutation() {
  const token = useAccessToken();
  return useMutation({
    mutationFn: (request: UpsertReverseSortRequest) =>
      fetchMutation<UpsertReverseSortResponse>(
        token,
        'reverse-sort',
        'POST',
        request,
      ),
  });
}

export const useEditFertilizationsMutation = () => {
  const token = useAccessToken();
  return useMutation({
    mutationFn: async (
      input: EditFertilizationRequest[],
    ): Promise<EditFertilizationPayload[]> => {
      const response = await fetch(`${getBaseUrl()}/api/v1/fertilization`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Tunnel-Authorization': `tunnel ${import.meta.env.VITE_DEV_TUNNEL_TOKEN}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(input),
      });
      if (!response.ok) throw new Error(await response.text());
      return response.json();
    },
  });
};
